import React from 'react';
import { History, X, Copy, Clock, CheckCircle2, XCircle, AlertTriangle, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminBroadcastHistory } from '@/hooks/admin/useAdminBroadcastHistory';

const statusMeta = {
  scheduled: { icon: Clock, label: 'Scheduled', cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-300 border-blue-500/30' },
  recurring: { icon: Repeat, label: 'Recurring', cls: 'bg-violet-500/10 text-violet-600 dark:text-violet-300 border-violet-500/30' },
  sending: { icon: Clock, label: 'Sending', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-300 border-amber-500/30' },
  sent: { icon: CheckCircle2, label: 'Sent', cls: 'bg-green-500/10 text-green-600 dark:text-green-300 border-green-500/30' },
  failed: { icon: XCircle, label: 'Failed', cls: 'bg-destructive/10 text-destructive border-destructive/30' },
  cancelled: { icon: AlertTriangle, label: 'Cancelled', cls: 'bg-muted text-muted-foreground border-border/50' },
};

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const describeRecurrence = (rec) => {
  if (!rec?.cadence) return null;
  if (rec.cadence === 'daily') return `daily · ${rec.time_utc} UTC`;
  if (rec.cadence === 'weekly') {
    const days = (rec.days_of_week || [])
      .slice()
      .sort()
      .map((d) => WEEKDAY_SHORT[d])
      .join('/');
    return `weekly · ${days || '?'} · ${rec.time_utc} UTC`;
  }
  return rec.cadence;
};

const StatusBadge = ({ status }) => {
  const m = statusMeta[status] || statusMeta.sent;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${m.cls}`}
      data-testid={`broadcast-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const BroadcastHistoryCard = React.forwardRef(({ enabled = true, onClone }, ref) => {
  const { items, loading, cancellingId, refresh, cancel } = useAdminBroadcastHistory({ enabled });

  React.useImperativeHandle(ref, () => ({ refresh }));

  return (
    <div
      className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50 space-y-4"
      data-testid="broadcast-history-card"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <History className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold leading-tight">Broadcast History</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Scheduled, recurring, sent and failed pushes — cancel, clone or check open/click counts.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="broadcast-history-refresh-btn"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {items.length === 0 ? (
        <div
          className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border/50 rounded-xl"
          data-testid="broadcast-history-empty"
        >
          No broadcasts yet. Your sent and scheduled pushes will appear here.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="broadcast-history-list">
          {items.map((b) => {
            const rpt = b.delivery_report || {};
            const isScheduled = b.status === 'scheduled';
            const isRecurring = b.status === 'recurring';
            const isCancellable = isScheduled || isRecurring;
            const recDesc = describeRecurrence(b.recurrence);
            const analytics = b.analytics || { delivered: 0, opened: 0, clicked: 0 };
            return (
              <li
                key={b.broadcast_id}
                className="p-3 rounded-xl bg-muted/30 border border-border/50 space-y-1.5"
                data-testid={`broadcast-row-${b.broadcast_id}`}
              >
                <div className="flex items-start gap-2 justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{b.title}</p>
                      <StatusBadge status={b.status} />
                      {b.parent_broadcast_id && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/50"
                          data-testid={`broadcast-from-recurring-${b.broadcast_id}`}
                        >
                          from recurring
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{b.body}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {isCancellable && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancel(b.broadcast_id)}
                        disabled={cancellingId === b.broadcast_id}
                        data-testid={`broadcast-cancel-${b.broadcast_id}`}
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">{isRecurring ? 'Stop recurring' : 'Cancel'}</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onClone?.(b)}
                      data-testid={`broadcast-clone-${b.broadcast_id}`}
                    >
                      <Copy className="h-4 w-4" />
                      <span className="sr-only">Clone</span>
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                  <span>audience: <span className="text-foreground">{b.audience}</span></span>
                  {isRecurring ? (
                    <>
                      <span data-testid={`broadcast-recurrence-${b.broadcast_id}`}>
                        repeat: <span className="text-foreground">{recDesc}</span>
                      </span>
                      {b.next_fire_at && (
                        <span>next: <span className="text-foreground">{formatDateTime(b.next_fire_at)}</span></span>
                      )}
                    </>
                  ) : isScheduled ? (
                    <span>scheduled: <span className="text-foreground">{formatDateTime(b.scheduled_for)}</span></span>
                  ) : b.status === 'cancelled' ? (
                    b.recurrence?.cadence ? (
                      <span>cancelled recurring · was <span className="text-foreground">{describeRecurrence(b.recurrence)}</span></span>
                    ) : (
                      <span>cancelled · was scheduled: <span className="text-foreground">{formatDateTime(b.scheduled_for || b.created_at)}</span></span>
                    )
                  ) : (
                    <span>sent: <span className="text-foreground">{formatDateTime(b.sent_at || b.created_at)}</span></span>
                  )}
                  {b.status === 'sent' && (
                    <>
                      <span>
                        delivered: <span className="text-foreground">{rpt.sent ?? 0}</span>/
                        <span className="text-foreground">{rpt.audience_size ?? 0}</span>
                      </span>
                      {(rpt.audience_size ?? 0) > 0 && (rpt.total ?? 0) === 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                          title="The audience exists but none of those users have a registered push token yet. Open the app on a mobile device and grant notification permission, or grant it in the web preview once web-push is enabled."
                          data-testid={`broadcast-no-devices-${b.broadcast_id}`}
                        >
                          no devices registered
                        </span>
                      )}
                    </>
                  )}
                  {(analytics.opened > 0 || analytics.clicked > 0) && (
                    <span
                      className="text-foreground"
                      data-testid={`broadcast-analytics-${b.broadcast_id}`}
                    >
                      opens <span className="font-medium">{analytics.opened}</span>
                      {' · '}
                      clicks <span className="font-medium">{analytics.clicked}</span>
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});

BroadcastHistoryCard.displayName = 'BroadcastHistoryCard';

export default BroadcastHistoryCard;
